import React from 'react';
import './LegalPages.css';

function PrivacyPolicy({ onClose }) {
  return (
    <div className="legal-page-overlay" onClick={onClose}>
      <div className="legal-page-content" onClick={(e) => e.stopPropagation()}>
        <div className="legal-page-header">
          <h1>Privacy Policy</h1>
          <button className="legal-page-close" onClick={onClose}>×</button>
        </div>
        
        <div className="legal-page-body">
          <p className="legal-last-updated">Last updated: January 1, 2025</p>
          
          <section>
            <h2>1. Introduction</h2>
            <p>Swift Notes ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Swift Notes application and services ("Service").</p>
            <p>By using our Service, you consent to the data practices described in this policy.</p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information</h3>
            <p>We may collect personally identifiable information that you voluntarily provide to us when you:</p>
            <ul>
              <li>Register for an account (email address, name)</li>
              <li>Subscribe to our paid services (billing information)</li>
              <li>Contact us for support (communication content)</li>
              <li>Participate in surveys or promotions</li>
            </ul>

            <h3>2.2 Content Data</h3>
            <p>When you use our Service, we collect and store:</p>
            <ul>
              <li>Notes and text content you create</li>
              <li>Files and images you upload</li>
              <li>Audio recordings from speech-to-text features</li>
              <li>Folder organization and preferences</li>
              <li>AI enhancement requests and results</li>
            </ul>

            <h3>2.3 Technical Information</h3>
            <p>We automatically collect certain information when you access our Service:</p>
            <ul>
              <li>Device information (type, operating system, browser)</li>
              <li>IP address and location data</li>
              <li>Usage patterns and feature interactions</li>
              <li>Log files and error reports</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li><strong>Service Provision:</strong> To provide, maintain, and improve our note-taking services</li>
              <li><strong>AI Enhancement:</strong> To process your content through AI systems for formatting and enhancement</li>
              <li><strong>Account Management:</strong> To create and manage your user account</li>
              <li><strong>Communication:</strong> To send you service-related notifications and updates</li>
              <li><strong>Payment Processing:</strong> To process subscription payments and manage billing</li>
              <li><strong>Security:</strong> To monitor for and prevent security threats and abuse</li>
              <li><strong>Analytics:</strong> To understand usage patterns and improve our services</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information in the following circumstances:</p>
            
            <h3>4.1 Service Providers</h3>
            <p>We may share your information with third-party service providers who assist us in:</p>
            <ul>
              <li>Cloud hosting and data storage</li>
              <li>Payment processing</li>
              <li>Email delivery services</li>
              <li>Analytics and performance monitoring</li>
              <li>AI processing services (Anthropic Claude API)</li>
            </ul>

            <h3>4.2 Legal Requirements</h3>
            <p>We may disclose your information if required to do so by law or in response to:</p>
            <ul>
              <li>Valid legal process (subpoenas, court orders)</li>
              <li>Government requests</li>
              <li>Protection of our rights and safety</li>
              <li>Investigation of potential violations</li>
            </ul>

            <h3>4.3 Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</p>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>We implement appropriate technical and organizational security measures to protect your information:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication systems</li>
              <li>Employee training on data protection practices</li>
              <li>Incident response procedures</li>
            </ul>
            <p>However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>We retain your information for as long as necessary to:</p>
            <ul>
              <li>Provide you with our services</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce agreements</li>
            </ul>
            <p>When you delete your account, we will delete your personal data within 30 days, except where longer retention is required by law.</p>
          </section>

          <section>
            <h2>7. Your Privacy Rights</h2>
            <p>Depending on your location, you may have the following rights regarding your personal information:</p>
            
            <h3>7.1 General Rights</h3>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Restriction:</strong> Request limitation of processing</li>
              <li><strong>Objection:</strong> Object to certain types of processing</li>
            </ul>

            <h3>7.2 GDPR Rights (EU Residents)</h3>
            <p>If you are in the European Union, you have additional rights under GDPR:</p>
            <ul>
              <li>Right to withdraw consent at any time</li>
              <li>Right to lodge a complaint with supervisory authorities</li>
              <li>Right to be informed about automated decision-making</li>
            </ul>

            <h3>7.3 CCPA Rights (California Residents)</h3>
            <p>California residents have rights under the California Consumer Privacy Act (CCPA):</p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to non-discrimination for exercising CCPA rights</li>
            </ul>
          </section>

          <section>
            <h2>8. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar technologies to:</p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Authenticate your login sessions</li>
              <li>Analyze usage patterns and improve our services</li>
              <li>Provide security features</li>
            </ul>
            <p>You can control cookies through your browser settings, but disabling certain cookies may affect the functionality of our Service.</p>
          </section>

          <section>
            <h2>9. Third-Party Services</h2>
            <p>Our Service may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these external services. We encourage you to review their privacy policies.</p>
            
            <h3>9.1 AI Processing</h3>
            <p>We use Anthropic's Claude API for AI enhancement features. Your content may be processed by Anthropic's systems in accordance with their privacy policy and data processing terms.</p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.</p>
          </section>

          <section>
            <h2>11. International Data Transfers</h2>
            <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for international transfers, including:</p>
            <ul>
              <li>Standard contractual clauses</li>
              <li>Adequacy decisions by relevant authorities</li>
              <li>Privacy frameworks and certifications</li>
            </ul>
          </section>

          <section>
            <h2>12. Changes to Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
            <ul>
              <li>Posting the updated policy on our website</li>
              <li>Sending an email notification to registered users</li>
              <li>Displaying a prominent notice in our application</li>
            </ul>
            <p>Your continued use of the Service after any changes indicates your acceptance of the updated Privacy Policy.</p>
          </section>

          <section>
            <h2>13. Legal Basis for Processing (GDPR)</h2>
            <p>For EU residents, our legal basis for processing personal information includes:</p>
            <ul>
              <li><strong>Contract:</strong> Processing necessary for service provision</li>
              <li><strong>Consent:</strong> Where you have given explicit consent</li>
              <li><strong>Legitimate Interests:</strong> For improving our services and security</li>
              <li><strong>Legal Obligation:</strong> For compliance with applicable laws</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy; 